import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Typography,
  Box,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { makeStyles } from '@mui/styles';

const useStyles = makeStyles((theme: any) => ({
  dialogPaper: {
    minHeight: '80vh',
    maxHeight: '90vh',
    minWidth: '70vw',
    maxWidth: '90vw',
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingRight: theme.spacing(1),
  },
  dialogContent: {
    padding: 0,
    paddingTop: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
  },
  pdfContainer: {
    width: '100%',
    height: '100%',
    minHeight: '70vh',
    border: 'none',
    pointerEvents: 'auto',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  errorContainer: {
    padding: theme.spacing(3),
    textAlign: 'center',
  },
  metadata: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: theme.spacing(2),
    padding: theme.spacing(1, 2),
    backgroundColor: theme.palette.grey[50],
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  metadataItem: {
    display: 'flex',
    gap: theme.spacing(0.5),
    alignItems: 'center',
  },
  metadataLabel: {
    fontWeight: 600,
    fontSize: '0.85rem',
  },
}));

interface IOrderFormPreviewModalProps {
  open: boolean;
  onClose: () => void;
  signedUrl: string;
  metadata?: {
    fileName?: string;
    completedDate?: string;
    fillCycleNumber?: number;
    orderId?: number;
  };
}

export const OrderFormPreviewModal: React.FC<IOrderFormPreviewModalProps> = ({
  open,
  onClose,
  signedUrl,
  metadata,
}) => {
  const classes = useStyles();
  const [error, setError] = useState<string | null>(null);

  const documentUrl = open && signedUrl ? signedUrl : null;
  const loading = false;

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      classes={{ paper: classes.dialogPaper }}
      aria-labelledby="order-form-preview-title"
    >
      <DialogTitle id="order-form-preview-title" className={classes.dialogTitle}>
        <Typography variant="h6">Last Order Form Preview</Typography>
        <IconButton onClick={handleClose} size="small" aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {metadata && (
        <Box className={classes.metadata}>
          {metadata.fileName && (
            <Box className={classes.metadataItem}>
              <Typography className={classes.metadataLabel}>File:</Typography>
              <Typography variant="body2">{metadata.fileName}</Typography>
            </Box>
          )}
          {metadata.completedDate && (
            <Box className={classes.metadataItem}>
              <Typography className={classes.metadataLabel}>Completed:</Typography>
              <Typography variant="body2">{formatDate(metadata.completedDate)}</Typography>
            </Box>
          )}
          {metadata.fillCycleNumber && (
            <Box className={classes.metadataItem}>
              <Typography className={classes.metadataLabel}>FC:</Typography>
              <Typography variant="body2">#{metadata.fillCycleNumber}</Typography>
            </Box>
          )}
          {metadata.orderId && (
            <Box className={classes.metadataItem}>
              <Typography className={classes.metadataLabel}>Order:</Typography>
              <Typography variant="body2">{metadata.orderId}</Typography>
            </Box>
          )}
        </Box>
      )}

      <DialogContent className={classes.dialogContent}>
        {loading && (
          <Box className={classes.loadingContainer}>
            <CircularProgress />
            <Typography>Loading order form...</Typography>
          </Box>
        )}

        {error && (
          <Box className={classes.errorContainer}>
            <Typography color="error" variant="h6" gutterBottom>
              Error Loading Document
            </Typography>
            <Typography color="textSecondary">{error}</Typography>
          </Box>
        )}

        {!loading && !error && documentUrl && (
          <iframe
            src={`${documentUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
            className={classes.pdfContainer}
            title="Order Form Preview"
          />
        )}

        {!loading && !error && !documentUrl && (
          <Box className={classes.errorContainer}>
            <Typography color="textSecondary">No document available</Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
